import { firstValueFrom, race } from 'rxjs'
import { inject, Injectable, NgZone } from '@angular/core'
import { TranslateService } from '@ngx-translate/core'
import { ActiveToast } from 'ngx-toastr'

import { APIService, BOOTSTRAP_DATA, HttpInterceptorService } from 'api'
import { NotifyService, WindowLocationService } from 'common/util'
import { SEQUENCE_ID_CUSTOM_FIELD } from 'panel/constants'
import { BridgeError } from 'panel/services/uxpBridge.service'
import { BaseInterface } from 'panel/interfaces/base'
import { CEPPanelInterface } from 'panel/interfaces/cep'
import { DummyInterface } from 'panel/interfaces/dummy'
import { MediaComposerInterface } from 'panel/interfaces/mediacomposer'
import { ResolvePanelInterface } from 'panel/interfaces/resolve'
import { UXPPanelInterface } from 'panel/interfaces/uxp'
import { UploaderService } from 'uploader'

interface PanelWindow extends Window {
  cep?: any
  mcapi?: any
  plugin?: any
  panel?: any
  nodeRequire?: any
  WorkflowIntegration?: any
  dummyPanelInterface?: any
  __ELEMENTS_UXP_PANEL__?: boolean
}
declare let window: PanelWindow

@Injectable({ providedIn: 'root' })
export class PanelService {
  plugin: BaseInterface
  ready: Promise<void>
  platform: string
  native = false
  active = false
  app: string
  fs: any
  isUxpPanel = this.detectUXPOnInitialNavigation()

  private disconnectedToast: ActiveToast<any> | null = null
  readonly isPanel = Boolean(inject(BOOTSTRAP_DATA).isPanel)

  constructor (
    private api: APIService,
    private interceptor: HttpInterceptorService,
    private notify: NotifyService,
    private translate: TranslateService,
    private uploader: UploaderService,
    private windowLocation: WindowLocationService,
    private zone: NgZone,
  ) {
    this.plugin = new DummyInterface()

    if (this.isUxpPanel) {
      this.plugin = new UXPPanelInterface()
    } else if (typeof window.cep !== typeof undefined) {
      this.plugin = new CEPPanelInterface()
    } else if (typeof window.WorkflowIntegration !== typeof undefined) {
      this.plugin = new ResolvePanelInterface()
    } else if (typeof window.mcapi !== typeof undefined) {
      this.plugin = new MediaComposerInterface()
    }

    window.plugin = this.plugin
    window.panel = this

    this.ready = this.plugin.init()
    this.native = this.plugin.native
    this.app = this.plugin.getApp()
    this.fs = this.plugin.getFS()
    this.platform = this.plugin.getPlatform()

    this.ready.then(() => {
      this.plugin.active$.subscribe(active => {
        this.zone.run(() => {
          this.active = active
          this.updateDisconnectedToast(active)
        })
      })

      if (this.plugin.active) {
        windowLocation.externalURIStrategy = {
          navigate: (uri) => this.plugin.openURL(uri),
        }
        this.interceptor.enableSessionExpiryDetection = false
        this.interceptor.sessionExpired$.subscribe(() => {
          if (this.native) {
            this.restartExtension()
          }
        })

        document.addEventListener('click', (event) => {
          const elem = event.target as HTMLElement
          if (elem.tagName !== 'A') {
            return
          }

          const anchor = event.target as HTMLAnchorElement
          const next = new URL(anchor.href, window.location.href)
          if (anchor.target === '_blank' || window.location.origin !== next.origin) {
            event.preventDefault()
            this.plugin.openURL(anchor.href)
          }
        }, false)
      }
    })
  }

  private detectUXPOnInitialNavigation (): boolean {
    try {
      const params = new URLSearchParams(window.location.search)
      if (params.get('uxp-panel') === '1') {
        return true
      }
    } catch {
      // Ignore malformed URLs.
    }
    return Boolean(window.__ELEMENTS_UXP_PANEL__)
  }

  private updateDisconnectedToast (active: boolean): void {
    if (!this.isUxpPanel) {
      return
    }
    if (active) {
      if (this.disconnectedToast) {
        this.notify.dismiss(this.disconnectedToast)
        this.disconnectedToast = null
      }
      return
    }
    this.disconnectedToast ??= this.notify.custom(
      'warning',
      this.translate.instant('Premiere Pro is not connected'),
      this.translate.instant('Open the ELEMENTS panel in Premiere Pro to continue.'),
      { sticky: true },
    )
  }

  async syncMetadata (): Promise<void> {
    const process = this.notify.process(this.translate.instant('Downloading metadata'))

    try {
      const paths = await this.plugin.getProjectPaths()

      const response = await this.api.private.locateMetadata({
        locateMetadataEndpointRequest: {
          paths,
        },
      })

      for (const item of response) {
        const existingMarkers = await this.plugin.getMarkersForPath(item.path)
        const key = m => `${m.title}%%%%${m.text}`
        const newKeys = new Set(item.markers.map(key))
        const newMarkers = item.markers.concat(existingMarkers.filter(m => !newKeys.has(key(m))))
        await this.plugin.replaceMetadataForPath(item.path, newMarkers, item.tags)
      }

      this.notify.finishProcess(process, 'success', this.translate.instant('Downloaded metadata'))
    } catch (e) {
      const error = e instanceof BridgeError
        ? new Error(e.userMessage || 'Sync failed')
        : e
      this.notify.errorFromResponse(this.translate.instant('Error'), error)
      this.notify.finishProcess(process, 'error', this.translate.instant('Failed'))
      throw error
    }
  }

  restartExtension (): void {
    this.plugin.restart()
  }

  async uploadActiveSequence (): Promise<void> {
    await this.plugin.renderSequence(
      async file => {
        const id = await this.plugin.getActiveSequenceID()
        if (id) {
          const path = this.uploader.currentConfig()!.path + '/' + file.name
          await this.api.main.registerUploadMetadata({
            registerUploadMetadataEndpointRequest: {
              items: [{
                path,
                customFields: {
                  [SEQUENCE_ID_CUSTOM_FIELD]: id,
                },
                tags: [],
              }],
            },
          })
        }
        this.uploader.flow.addFile(file as any)
        await firstValueFrom(race(this.uploader.complete$, this.uploader.cancelled$))
      },
    )
  }
}
